/**
 * Billing Configuration Service
 * Fetches billing configuration from backend API
 * This replaces hardcoded constants with dynamic configuration
 */

import apiClient from './apiClient';
import { BASE_PLAN_PRICING, SEAT_PRICING, PHONE_ADDON, MAX_SEATS, ANNUAL_DISCOUNT } from '../constants';

let billingConfig = null;
let configPromise = null;

/**
 * Fetch billing configuration from backend
 * Implements caching to avoid repeated API calls
 */
export const fetchBillingConfig = async () => {
  if (billingConfig) {
    return billingConfig;
  }

  if (configPromise) {
    return configPromise;
  }

  configPromise = (async () => {
    try {
      const response = await apiClient.get('/api/billing/config');
      
      if (response.data.success) {
        billingConfig = response.data.data;
        return billingConfig;
      } else {
        throw new Error('Failed to fetch billing configuration');
      }
    } catch (error) {
      console.error('Error fetching billing configuration:', error);
      // Fallback to cached constants if API fails
      return getFallbackConfig();
    } finally {
      configPromise = null;
    }
  })();

  return configPromise;
};

/**
 * Get plan configuration by plan type
 */
export const getPlanConfig = async (planType) => {
  const config = await fetchBillingConfig();
  return config.plans[planType];
};

/**
 * Get all available plans
 */
export const getAllPlans = async () => {
  const config = await fetchBillingConfig();
  return config.plans;
};

/**
 * Get plans by category (individual, team, brokerage)
 */
export const getPlansByCategory = async (category) => {
  const config = await fetchBillingConfig();
  const planTypes = config.plan_categories[category] || [];
  const plans = {};
  
  planTypes.forEach(planType => {
    plans[planType] = config.plans[planType];
  });
  
  return plans;
};

/**
 * Calculate annual price with discount
 */
export const getAnnualPrice = async (planType) => {
  const config = await fetchBillingConfig();
  const plan = config.plans[planType];
  
  if (!plan) return 0;
  
  const monthlyPrice = plan.monthly_cost;
  const discount = plan.annual_discount || config.pricing.annual_discount_rate;
  
  return monthlyPrice * 12 * (1 - discount);
};

/**
 * Get pricing configuration
 */
export const getPricingConfig = async () => {
  const config = await fetchBillingConfig();
  return config.pricing;
};

/**
 * Get phone addon configuration
 */
export const getPhoneConfig = async () => {
  const config = await fetchBillingConfig();
  return config.phone_config;
};

/**
 * Clear cached configuration (useful for forcing refresh)
 */
export const clearBillingConfigCache = () => {
  billingConfig = null;
  configPromise = null;
};

/**
 * Fallback configuration (uses constants.js values)
 * Used when API is unavailable
 */
const getFallbackConfig = () => {
  return {
    plans: {
      'Solo': {
        name: 'Solo',
        base_seats: SEAT_PRICING.BASE_PLAN_INCLUDED_SEATS.solo,
        monthly_cost: BASE_PLAN_PRICING.solo,
        annual_discount: ANNUAL_DISCOUNT,
        features: ['Calendar sync', 'Basic CRM', 'Email support'],
        max_seats: MAX_SEATS.solo,
        phone_slots: { 
          max: PHONE_ADDON.MAX_SLOTS_PER_ACCOUNT.solo, 
          recommended: PHONE_ADDON.RECOMMENDED_SLOTS.solo 
        }
      },
      'Solo_Assistant': {
        name: 'Solo + Assistant',
        base_seats: SEAT_PRICING.BASE_PLAN_INCLUDED_SEATS.solo_assistant,
        monthly_cost: BASE_PLAN_PRICING.solo_assistant,
        annual_discount: ANNUAL_DISCOUNT,
        features: ['Everything in Solo', 'Assistant access', 'Priority support'],
        max_seats: MAX_SEATS.solo_assistant,
        phone_slots: { 
          max: PHONE_ADDON.MAX_SLOTS_PER_ACCOUNT.solo_assistant, 
          recommended: PHONE_ADDON.RECOMMENDED_SLOTS.solo_assistant 
        }
      },
      'Small_Team': {
        name: 'Small Team',
        base_seats: SEAT_PRICING.BASE_PLAN_INCLUDED_SEATS.small_team,
        monthly_cost: BASE_PLAN_PRICING.small_team,
        annual_discount: ANNUAL_DISCOUNT,
        features: ['Everything in Solo+', 'Team collaboration', 'Advanced reporting'],
        max_seats: MAX_SEATS.small_team,
        phone_slots: { 
          max: PHONE_ADDON.MAX_SLOTS_PER_ACCOUNT.small_team, 
          recommended: PHONE_ADDON.RECOMMENDED_SLOTS.small_team 
        }
      },
      'Medium_Team': {
        name: 'Medium Team',
        base_seats: SEAT_PRICING.BASE_PLAN_INCLUDED_SEATS.medium_team,
        monthly_cost: BASE_PLAN_PRICING.medium_team,
        annual_discount: ANNUAL_DISCOUNT,
        features: ['Everything in Small Team', 'Advanced integrations', 'Custom workflows'],
        max_seats: MAX_SEATS.medium_team,
        phone_slots: { 
          max: PHONE_ADDON.MAX_SLOTS_PER_ACCOUNT.medium_team, 
          recommended: PHONE_ADDON.RECOMMENDED_SLOTS.medium_team 
        }
      },
      'Large_Team': {
        name: 'Large Team',
        base_seats: SEAT_PRICING.BASE_PLAN_INCLUDED_SEATS.large_team,
        monthly_cost: BASE_PLAN_PRICING.large_team,
        annual_discount: ANNUAL_DISCOUNT,
        features: ['Everything in Medium Team', 'Enterprise security', 'Dedicated support'],
        max_seats: MAX_SEATS.large_team,
        phone_slots: { 
          max: PHONE_ADDON.MAX_SLOTS_PER_ACCOUNT.large_team, 
          recommended: PHONE_ADDON.RECOMMENDED_SLOTS.large_team 
        }
      },
      'Small_Brokerage': {
        name: 'Small Brokerage',
        base_seats: SEAT_PRICING.BASE_PLAN_INCLUDED_SEATS.small_brokerage,
        monthly_cost: BASE_PLAN_PRICING.small_brokerage,
        annual_discount: ANNUAL_DISCOUNT,
        features: ['Team features', 'Brokerage management', 'Multi-location'],
        max_seats: MAX_SEATS.small_brokerage,
        phone_slots: { 
          max: PHONE_ADDON.MAX_SLOTS_PER_ACCOUNT.small_brokerage, 
          recommended: PHONE_ADDON.RECOMMENDED_SLOTS.small_brokerage 
        }
      },
      'Medium_Brokerage': {
        name: 'Medium Brokerage',
        base_seats: SEAT_PRICING.BASE_PLAN_INCLUDED_SEATS.medium_brokerage,
        monthly_cost: BASE_PLAN_PRICING.medium_brokerage,
        annual_discount: ANNUAL_DISCOUNT,
        features: ['Small Brokerage features', 'Advanced analytics', 'Custom workflows'],
        max_seats: MAX_SEATS.medium_brokerage,
        phone_slots: { 
          max: PHONE_ADDON.MAX_SLOTS_PER_ACCOUNT.medium_brokerage, 
          recommended: PHONE_ADDON.RECOMMENDED_SLOTS.medium_brokerage 
        }
      },
      'Large_Brokerage': {
        name: 'Large Brokerage',
        base_seats: SEAT_PRICING.BASE_PLAN_INCLUDED_SEATS.large_brokerage,
        monthly_cost: BASE_PLAN_PRICING.large_brokerage,
        annual_discount: ANNUAL_DISCOUNT,
        features: ['Medium Brokerage features', 'Enterprise security', 'Dedicated support'],
        max_seats: MAX_SEATS.large_brokerage,
        phone_slots: { 
          max: PHONE_ADDON.MAX_SLOTS_PER_ACCOUNT.large_brokerage, 
          recommended: PHONE_ADDON.RECOMMENDED_SLOTS.large_brokerage 
        }
      }
    },
    pricing: {
      seat_addon_cost: SEAT_PRICING.MONTHLY_COST_PER_SEAT,
      phone_slot_cost: PHONE_ADDON.MONTHLY_COST_PER_SLOT,
      annual_discount_rate: ANNUAL_DISCOUNT,
      tax_rate: 0.08,
      trial_period_days: 14,
      billing_retry_days: [3, 7, 14],
      currency: 'usd'
    },
    phone_config: {
      monthly_cost_per_slot: PHONE_ADDON.MONTHLY_COST_PER_SLOT,
      included_minutes: PHONE_ADDON.INCLUDED_MINUTES,
      overage_rate_per_minute: PHONE_ADDON.OVERAGE_RATE_PER_MINUTE,
      features: PHONE_ADDON.FEATURES
    },
    billing_cycles: ['monthly', 'annual'],
    plan_categories: {
      individual: ['Solo', 'Solo_Assistant'],
      team: ['Small_Team', 'Medium_Team', 'Large_Team'],
      brokerage: ['Small_Brokerage', 'Medium_Brokerage', 'Large_Brokerage']
    }
  };
};

const billingConfigService = {
  fetchBillingConfig,
  getPlanConfig,
  getAllPlans,
  getPlansByCategory,
  getAnnualPrice,
  getPricingConfig,
  getPhoneConfig,
  clearBillingConfigCache
};

export default billingConfigService;
